import React from 'react'
import { C } from '../styles/tokens'
import { SleepTracker } from '../native/sleepTracker'

let Capacitor = null
try { Capacitor = require('@capacitor/core').Capacitor } catch (e) {}
const isNative = () => !!(Capacitor && Capacitor.isNativePlatform && Capacitor.isNativePlatform())

const fmtDur = (min) => {
  const h = Math.floor(min / 60), m = Math.round(min % 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}
const fmtClock = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'

export default function SleepCard() {
  const [state, setState] = React.useState('init') // init | web | idle | tracking | done
  const [start, setStart] = React.useState(0)
  const [night, setNight] = React.useState(null)   // last night's summary
  const [history, setHistory] = React.useState([])
  const [elapsed, setElapsed] = React.useState(0)
  const [busy, setBusy] = React.useState(false)

  const loadHistory = React.useCallback(async () => {
    try { const { history } = await SleepTracker.getHistory(); setHistory(history || []); if ((history || []).length) setNight(history[history.length - 1]) } catch (e) {}
  }, [])

  const init = React.useCallback(async () => {
    if (!isNative() || !SleepTracker) { setState('web'); return }
    try {
      const s = await SleepTracker.status()
      if (s && s.tracking) { setStart(s.start || Date.now()); setState('tracking') }
      else { setState('idle') }
      await loadHistory()
    } catch (e) { setState('idle') }
  }, [loadHistory])

  React.useEffect(() => { init() }, [init])

  // live elapsed timer while tracking
  React.useEffect(() => {
    if (state !== 'tracking') return
    const id = setInterval(() => setElapsed(Date.now() - start), 1000)
    return () => clearInterval(id)
  }, [state, start])

  const begin = async () => {
    setBusy(true)
    try { const s = await SleepTracker.start(); setStart(s.start || Date.now()); setState('tracking') }
    catch (e) {} finally { setBusy(false) }
  }

  const finish = async () => {
    setBusy(true)
    try { const { night } = await SleepTracker.stop(); setNight(night); setState('done'); await loadHistory() }
    catch (e) {} finally { setBusy(false) }
  }

  const wrap = (children) => (
    <div style={{ background: 'white', borderRadius: 14, border: `1px solid ${C.border}`, overflow: 'hidden', boxShadow: '0 2px 10px rgba(13,34,24,.05)', marginTop: '1.25rem' }}>
      <div style={{ background: C.headerGrad, padding: '.9rem 1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1.1rem' }}>😴</span>
        <div>
          <div style={{ fontWeight: 700, color: 'white', fontSize: '.9rem' }}>Sleep</div>
          <div style={{ fontSize: '.66rem', color: 'rgba(255,255,255,.6)', marginTop: 1 }}>Estimated from phone movement</div>
        </div>
      </div>
      <div style={{ padding: '1.25rem' }}>{children}</div>
    </div>
  )

  const btn = (label, onClick, primary = true) => (
    <button onClick={onClick} disabled={busy}
      style={{ width: '100%', padding: '.8rem', borderRadius: 10, border: primary ? 'none' : `1.5px solid ${C.g600}`, background: primary ? C.g700 : 'white', color: primary ? 'white' : C.g700, fontWeight: 700, fontSize: '.88rem', cursor: busy ? 'default' : 'pointer', fontFamily: "'DM Sans',sans-serif" }}>
      {busy ? 'Please wait…' : label}
    </button>
  )

  const note = (t) => <div style={{ fontSize: '.8rem', color: C.muted, lineHeight: 1.6, marginBottom: '1rem' }}>{t}</div>

  if (state === 'init') return wrap(<div style={{ textAlign: 'center', color: C.muted, fontSize: '.85rem' }}>Loading…</div>)

  if (state === 'web') return wrap(note('😴 Sleep tracking runs in the Revive app (Android). Open the app on your phone to estimate your sleep overnight.'))

  const summary = (n) => n && (
    <div style={{ marginTop: state === 'done' ? '1rem' : 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'center' }}>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '2rem', color: C.g800 }}>{fmtDur(n.asleepMin)}</div>
        <div style={{ fontSize: '.75rem', color: C.muted }}>estimated sleep</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '.9rem', textAlign: 'center' }}>
        <div><div style={{ fontWeight: 700, color: C.text }}>{n.efficiency}%</div><div style={{ fontSize: '.62rem', color: C.muted }}>efficiency</div></div>
        <div><div style={{ fontWeight: 700, color: C.text }}>{fmtDur(n.inBedMin)}</div><div style={{ fontSize: '.62rem', color: C.muted }}>in bed</div></div>
        <div><div style={{ fontWeight: 700, color: C.text }}>{n.awakenings}</div><div style={{ fontSize: '.62rem', color: C.muted }}>awakenings</div></div>
      </div>
      <div style={{ fontSize: '.62rem', color: C.muted, textAlign: 'center', marginTop: '.7rem' }}>{fmtClock(n.start)} – {fmtClock(n.end)} · estimate, not medical-grade</div>
    </div>
  )

  if (state === 'tracking') return wrap(<>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '.72rem', color: C.g600, marginBottom: '.4rem', display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.g500, display: 'inline-block' }} /> Tracking since {fmtClock(start)}
      </div>
      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.8rem', color: C.g800, marginBottom: '.3rem' }}>{fmtDur(elapsed / 60000)}</div>
      <div style={{ fontSize: '.72rem', color: C.muted, marginBottom: '1rem' }}>Keep your phone on the bed. Tap below when you wake up.</div>
    </div>
    {btn('I’m awake — stop & save', finish)}
  </>)

  if (state === 'done') return wrap(<>
    {summary(night)}
    <div style={{ marginTop: '1.1rem' }}>{btn('Track again tonight', () => setState('idle'), false)}</div>
  </>)

  // idle
  return wrap(<>
    {note('Tap Start when you go to bed and place your phone on the mattress. Revive estimates your sleep from movement overnight. For accurate stages, use a wearable.')}
    {night && <div style={{ marginBottom: '1rem' }}>{summary(night)}</div>}
    {btn('Start sleep tracking', begin)}
  </>)
}
